import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../../../src/components/DashboardLayout/DasboardLayout";
import { API_URL } from "../../../src/components/config";
import { Trophy, TrendingUp, Users, RefreshCw, Download, Crown } from "lucide-react";

const TopTwentyCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("tripCount");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, tripsRes, rideshareRequestsRes] = await Promise.all([
        fetch(`${API_URL}/customerdetails/`),
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/rideshare/requests/all`)
      ]);

      const [customersData, tripsData, rideshareRequestsData] = await Promise.all([
        customersRes.json(),
        tripsRes.json(),
        rideshareRequestsRes.json()
      ]);

      setCustomers(customersData || []);
      setTrips(tripsData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const topCustomersData = useMemo(() => {
    const customersWithMetrics = customers.map(customer => {
      // Real key is `customerid` — customer_details rows have no `cust_id` field at all.
      const customerTrips = trips.filter(trip => trip.cust_id === customer.customerid);
      const customerRideshareRequests = rideshareRequests.filter(
        req => req.passenger_id === customer.customerid
      );

      const privateValue = customerTrips.reduce((sum, trip) =>
        sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0
      );
      const rideshareValue = customerRideshareRequests.reduce((sum, req) =>
        sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0
      );

      const tripCount = customerTrips.length + customerRideshareRequests.length;
      const totalValue = privateValue + rideshareValue;
      const avgValue = tripCount > 0 ? totalValue / tripCount : 0;

      return {
        ...customer,
        tripCount,
        totalValue,
        avgValue
      };
    });

    return customersWithMetrics
      .filter(c => c.tripCount > 0)
      .sort((a, b) => {
        switch(sortBy) {
          case "tripCount":
            return b.tripCount - a.tripCount;
          case "totalValue":
            return b.totalValue - a.totalValue;
          case "avgValue":
            return b.avgValue - a.avgValue;
          default:
            return b.tripCount - a.tripCount;
        }
      })
      .slice(0, 20);
  }, [customers, trips, rideshareRequests, sortBy]);

  const exportData = () => {
    const csv = [
      ['Rank', 'Customer ID', 'Name', 'Email', 'Phone', 'Username', 'Trips Requested', 'Total Spent', 'Avg Spent', 'Membership'],
      ...topCustomersData.map((customer, index) => [
        index + 1,
        customer.customerid,
        `${customer.first_name} ${customer.last_name}`,
        customer.email,
        customer.phone,
        customer.username,
        customer.tripCount,
        customer.totalValue.toFixed(2),
        customer.avgValue.toFixed(2),
        customer.membership_status || 'Basic'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'top_20_customers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Crown className="text-purple-500" size={32} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Top 20 Performing Customers
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Highest performing customers by {sortBy === 'tripCount' ? 'trip count' : sortBy === 'totalValue' ? 'total spending' : 'average spending'}
              </p>
            </div>
          </div>
          <button
            onClick={exportData}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Download size={16} />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Sort Options */}
        <div className="flex space-x-2 mb-6">
          {[
            { value: "tripCount", label: "Trip Count", icon: Users },
            { value: "totalValue", label: "Total Spending", icon: TrendingUp },
            { value: "avgValue", label: "Avg Spending", icon: TrendingUp }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                sortBy === option.value
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              <option.icon size={16} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Trips</p>
                <p className="text-2xl font-bold">
                  {topCustomersData.reduce((sum, c) => sum + c.tripCount, 0)}
                </p>
              </div>
              <Users size={24} className="opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Revenue</p>
                <p className="text-2xl font-bold">
                  ${topCustomersData.reduce((sum, c) => sum + c.totalValue, 0).toFixed(0)}
                </p>
              </div>
              <TrendingUp size={24} className="opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Avg Performance</p>
                <p className="text-2xl font-bold">
                  {(topCustomersData.reduce((sum, c) => sum + c.tripCount, 0) / topCustomersData.length).toFixed(1)}
                </p>
              </div>
              <Crown size={24} className="opacity-80" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-6 py-4">Rank</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Account</th>
                <th className="px-6 py-4">Performance</th>
                <th className="px-6 py-4">Spending</th>
                <th className="px-6 py-4">Membership</th>
              </tr>
            </thead>
            <tbody>
              {topCustomersData.map((customer, index) => (
                <tr 
                  key={customer.customerid}
                  className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    index < 3 ? 'bg-gradient-to-r from-purple-50 to-transparent dark:from-purple-900/20' : ''
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-bold text-gray-500">#{index + 1}</span>
                      {index === 0 && <span className="text-2xl">👑</span>}
                      {index === 1 && <span className="text-2xl">🥈</span>}
                      {index === 2 && <span className="text-2xl">🥉</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {customer.first_name} {customer.last_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {customer.customerid}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-xs">{customer.email}</p>
                      <p className="text-xs">{customer.phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs">
                      <p className="font-medium">@{customer.username}</p>
                      <p className="text-gray-500">Since {new Date(customer.created_at).getFullYear()}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-purple-600">{customer.tripCount}</p>
                      <p className="text-xs text-gray-500">trips</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-green-600">
                        ${customer.totalValue.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">
                        avg: ${customer.avgValue.toFixed(2)}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      customer.membership_status === 'Premium' 
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                        : customer.membership_status === 'Gold'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                    }`}>
                      {customer.membership_status || 'Basic'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {topCustomersData.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500 dark:text-gray-400">
              No customer performance data available
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TopTwentyCustomers;
